import React, { useMemo, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  parseAndFormatDate,
  formatCurrency,
  formatCpfCnpj,
  bytesFormat,
} from '@utils/formatters';
import { Col, Row, Descriptions, Popover, Button, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import ConsumptionCard from '@components/ConsumptionCard';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import { IObject } from '@utils/defaultTypes';
import { ITableTabs } from '@components/Table';
import CustomCard from '@components/CustomCard';
import InvoiceStats from '@components/InvoiceStandardDetails/InvoiceStats';
import moment from 'moment';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CsvDetailedInvoice from '../CsvDetailed';
import PdfDocument from '../PdfDetailed';
import InvoiceItemsMot from '../InvoiceItemsMot';

interface IConsolidatedInvoice {
  searchParams: IObject;
  setSearchParams: any;
  setInvoiceStatus: React.Dispatch<boolean>;
}

const ConsolidatedInvoice = ({
  searchParams,
  setSearchParams,
  setInvoiceStatus,
}: IConsolidatedInvoice) => {
  const { t } = useTranslation(['invoices', 'attributes', 'requests']);
  const { keycloak } = useContext(KeycloakContext);
  const customerIdLogged = keycloak?.idTokenParsed?.customerId;
  const params = useParams();

  const { itemsType, customerId, invoiceId } = useMemo(
    () => ({
      itemsType: searchParams?.itemsType,
      customerId: params?.customerId,
      invoiceId: params?.invoiceId,
    }),
    [params?.customerId, params?.invoiceId, searchParams?.itemsType],
  );
  const isMyInvoice = customerIdLogged?.toString() === customerId;

  const { data } = useSwr(
    `/service-proxy/broker/${customerId}/invoices/${invoiceId}`,
  );

  const associatedTicket = useMemo(
    () => data?.associatedTicket,
    [data?.associatedTicket],
  );

  const invoiceDetails = useMemo(
    () =>
      data && {
        ...data?.dadosFatura,
        nuQtdItens: data?.nuQtdItens,
        opened: data?.dadosFatura?.idStatusFatura === 'O',
      },
    [data],
  );

  useEffect(() => {
    if (invoiceDetails) {
      setInvoiceStatus(invoiceDetails?.opened);
    }
  }, [invoiceDetails, setInvoiceStatus]);

  const consumption = useMemo(() => {
    const balance = data?.dadosSaldo;

    return {
      totalFranchise: balance?.nuFranquiaTotal,
      franchiseConsumption: balance?.nuConsumoFranquia,
      surplusConsumption: balance?.nuConsumoExcedente,
      totalConsumption: balance?.nuConsumoTotal,
    };
  }, [data]);

  const listItems = useMemo(() => {
    const typeItem = itemsType === 'loose' ? 'S' : 'N';
    return data?.listaItens?.filter(
      (item: { blItemEdit: string }) => item.blItemEdit === typeItem,
    );
  }, [data, itemsType]);

  const invoicePeriodFormatted = useMemo(
    () =>
      invoiceDetails?.dataInicio &&
      `${parseAndFormatDate(
        invoiceDetails?.dataInicio,
        'dd-MM-yyyy',
        'dd/MM/yyyy',
      )} ${t('invoice.to')}
          ${parseAndFormatDate(
            invoiceDetails?.dataTermino,
            'dd-MM-yyyy',
            'dd/MM/yyyy',
          )}`,
    [invoiceDetails?.dataInicio, invoiceDetails?.dataTermino, t],
  );

  const invoiceExpirationDateFormatted = useMemo(
    () =>
      parseAndFormatDate(
        invoiceDetails?.dataVencimento,
        'dd-MM-yyyy',
        'dd/MM/yyyy',
      ),
    [invoiceDetails?.dataVencimento],
  );

  const totalTrafficFormatted = useMemo(
    () =>
      invoiceDetails?.nuTrafegoTotal &&
      bytesFormat(invoiceDetails?.nuTrafegoTotal),
    [invoiceDetails?.nuTrafegoTotal],
  );

  const terminalsFormatted = useMemo(
    () =>
      invoiceDetails?.nuTerminais &&
      `${invoiceDetails?.nuTerminais ? invoiceDetails?.nuTerminais : ''}
      (${invoiceDetails?.nuTerminaisUso} ${t('invoice.inUse')})`,
    [invoiceDetails, t],
  );

  const customerDescriptions = useMemo(() => {
    const customerData = data?.customer;
    return [
      {
        label: t('attributes:id'),
        value: customerData?.id,
      },
      {
        label: t('attributes:name'),
        value: customerData?.name,
      },
      {
        label: t('attributes:tradingName'),
        value: customerData?.tradingName,
      },
      {
        label: t('attributes:cpfCnpj'),
        value: formatCpfCnpj(customerData?.cpfCnpj),
      },
    ];
  }, [data?.customer, t]);

  const stats = useMemo(
    () => ({
      value:
        invoiceDetails?.nuValorFatura &&
        formatCurrency(invoiceDetails?.nuValorFatura),
      expirationDate: invoiceExpirationDateFormatted,
      totalTraffic: totalTrafficFormatted,
      terminals: terminalsFormatted,
      period: invoicePeriodFormatted,
      number: invoiceDetails?.idFatura,
    }),
    [
      invoiceDetails?.idFatura,
      invoiceDetails?.nuValorFatura,
      invoiceExpirationDateFormatted,
      invoicePeriodFormatted,
      terminalsFormatted,
      totalTrafficFormatted,
    ],
  );

  const invoiceItemsTabs = useMemo(
    (): ITableTabs => ({
      activeKey: itemsType,
      defaultActiveKey: 'consolidated',
      list: [
        {
          label: t('invoice.tabs.items-consolidated'),
          key: 'consolidated',
        },
        {
          label: t('invoice.tabs.items-loose'),
          key: 'loose',
        },
      ],
      onChange: (activeKey: string) => {
        setSearchParams({ itemsType: activeKey });
      },
    }),
    [itemsType, setSearchParams, t],
  );

  const normalizeUrl = (url: string) => {
    if (!url) return '';

    const value = String(url).trim();

    if (/^https?:\/\//i.test(value)) return value;

    return `https://${value}`;
  };

  return (
    <>
      <Row justify="center" style={{ marginTop: 24 }}>
        {associatedTicket && (
          <Popover
            content={
              <div style={{ textAlign: 'center' }}>
                <Descriptions
                  size="small"
                  style={{ width: 220 }}
                  layout="vertical"
                  column={1}
                  labelStyle={{
                    margin: '0 auto',
                  }}
                  contentStyle={{
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Descriptions.Item
                    label={t('attributes:associatedBill.dueDate')}
                    style={{
                      padding: '4px 0',
                    }}
                  >
                    {moment(associatedTicket?.dueDate).format('DD/MM/YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Status"
                    style={{
                      padding: '4px 0',
                    }}
                  >
                    {t(
                      `attributes:associatedBill.status.${associatedTicket?.status}`,
                    )}
                  </Descriptions.Item>
                </Descriptions>
                <Divider style={{ marginTop: 16, marginBottom: 12 }} />
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    window.open(normalizeUrl(associatedTicket?.link), '_blank');
                  }}
                >
                  {t('attributes:associatedBill.seeBill')}
                </Button>
              </div>
            }
            trigger={['click']}
          >
            <Button
              type="primary"
              icon={<InfoCircleOutlined />}
              style={{ margin: '0 8px' }}
            >
              {t('attributes:associatedBill.bill')}
            </Button>
          </Popover>
        )}
        {invoiceDetails && !invoiceDetails?.opened && (
          <RoleChecker role={rolesConstants.DOWNLOAD_INVOICES}>
            <div>
              <PdfDocument customerId={customerId} invoiceId={invoiceId} />
              <CsvDetailedInvoice
                customerId={customerId}
                invoiceId={invoiceId}
              />
            </div>
          </RoleChecker>
        )}
      </Row>

      <Row
        style={{
          margin: '16px 0',
        }}
      >
        <CustomCard>
          <Descriptions size="small">
            {customerDescriptions?.map(
              ({ label, value }) =>
                value && (
                  <Descriptions.Item
                    key={label}
                    label={label}
                    style={{
                      padding: '4px 0',
                    }}
                  >
                    {value}
                  </Descriptions.Item>
                ),
            )}
          </Descriptions>
        </CustomCard>
      </Row>

      <InvoiceStats data={stats} />

      <Row
        style={{
          margin: '16px 0',
        }}
      >
        <Col lg={24} md={24} xl={24} xs={24}>
          <ConsumptionCard data={consumption} />
        </Col>
      </Row>

      <InvoiceItemsMot
        data={listItems}
        customerId={customerId}
        invoiceId={invoiceId}
        canManageItems={!isMyInvoice}
        canAddItems={!isMyInvoice}
        tabs={invoiceItemsTabs}
      />
    </>
  );
};

export default ConsolidatedInvoice;
